export interface OfferConfig {
	// Timing Configuration
	timing: {
		/** Minimum time player must play before first offer appears (in milliseconds) */
		minGameTimeForOffer: number;
		/** Cooldown between offers (in milliseconds) */
		offerCooldown: number;
		/** Individual reward cooldowns (in milliseconds) */
		rewardCooldowns: Record<string, number>;
	};

	// Reward Configuration
	rewards: {
		coins: {
			/** Base amount of coins to award */
			baseAmount: number;
			/** Multiplier applied to passive income */
			passiveIncomeMultiplier: number;
			/** Round coins to nearest thousand */
			roundToThousand: boolean;
		};
		gems: {
			/** Fixed amount of gems to award */
			amount: number;
		};
		clicker2x: {
			/** Amount of 2x clickers to award */
			amount: number;
		};
		clicker3x: {
			/** Amount of 3x clickers to award */
			amount: number;
		};
		clicker5x: {
			/** Amount of 5x clickers to award */
			amount: number;
		};
		chests: {
			/** Number of chests to spawn on map */
			amount: number;
		};
		superClickers: {
			/** Amount of super clickers to award */
			amount: number;
		};
	};

	// Display Configuration
	display: {
		/** Icon names for each reward type */
		icons: Record<string, string>;
		/** Display names for each reward type */
		descriptions: Record<string, string>;
	};
}

// Default configuration that game designers can modify
export const defaultOfferConfig: OfferConfig = {
	timing: {
		// 3 minutes minimum play time before offers
		minGameTimeForOffer: 3 * 60 * 1_000,
		// 5 minutes between offers
		offerCooldown: 5 * 60 * 1_000,
		// Individual reward cooldowns
		rewardCooldowns: {
			coins: 2 * 60 * 60 * 1_000, // 2 hours
			gems: 12 * 60 * 60 * 1_000, // 12 hours
			clicker2x: 2 * 60 * 60 * 1_000, // 2 hours
			clicker3x: 2 * 60 * 60 * 1_000, // 2 hours
			clicker5x: 2 * 60 * 60 * 1_000, // 2 hours
			chests: 2 * 60 * 60 * 1_000, // 2 hours
			superClickers: 2 * 60 * 60 * 1_000, // 2 hours
			eventium: 0.5 * 60 * 60 * 1_000, // 0.5 hour
		},
	},

	rewards: {
		coins: {
			baseAmount: 25000,
			passiveIncomeMultiplier: 25,
			roundToThousand: true,
		},
		gems: {
			amount: 10,
		},
		clicker2x: {
			amount: 300,
		},
		clicker3x: {
			amount: 150,
		},
		clicker5x: {
			amount: 75,
		},
		chests: {
			amount: 3,
		},
		superClickers: {
			amount: 35,
		},
	},

	display: {
		icons: {
			coins: "coins",
			gems: "gems",
			clicker2x: "clicker_2",
			clicker3x: "clicker_3",
			clicker5x: "clicker_5",
			chests: "chest",
			superClickers: "super_clicker",
			eventium: "eventium",
		},
		descriptions: {
			coins: "Coins",
			gems: "Gems",
			clicker2x: "2x Clicker",
			clicker3x: "3x Clicker",
			clicker5x: "5x Clicker",
			chests: "Chests",
			superClickers: "Super Clickers",
			eventium: "Eventium",
		},
	},
};

// Konfigurácia pre skupinu B
export const groupBOfferConfig: OfferConfig = {
	timing: {
		minGameTimeForOffer: 3 * 60 * 1_000,
		offerCooldown: 3 * 60 * 1_000,
		rewardCooldowns: {
			coins: 1 * 60 * 60 * 1_000, // 1 hour
			gems: 3 * 60 * 60 * 1_000, // 3 hours
			clicker2x: 2 * 60 * 60 * 1_000, // 2 hours
			clicker3x: 2 * 60 * 60 * 1_000, // 2 hours
			clicker5x: 1 * 60 * 60 * 1_000, // 1 hour
			chests: 1 * 60 * 60 * 1_000, // 1 hour
			superClickers: 1 * 60 * 60 * 1_000, // 1 hour
			eventium: 0.5 * 60 * 60 * 1_000, // 0.5 hour
		},
	},

	rewards: {
		coins: {
			baseAmount: 30000,
			passiveIncomeMultiplier: 20,
			roundToThousand: true,
		},
		gems: {
			amount: 10,
		},
		clicker2x: {
			amount: 300,
		},
		clicker3x: {
			amount: 150,
		},
		clicker5x: {
			amount: 75,
		},
		chests: {
			amount: 3,
		},
		superClickers: {
			amount: 30,
		},
	},

	display: defaultOfferConfig.display, // Ikony a popisy ostávajú rovnaké ako v A
};

// Development/Testing configuration with shorter cooldowns
export const devOfferConfig: OfferConfig = {
	...defaultOfferConfig,
	timing: {
		minGameTimeForOffer: 0.2 * 60 * 1_000, // 12 seconds for testing
		offerCooldown: 1 * 60 * 1_000, // 1 minutes
		rewardCooldowns: {
			coins: 5 * 60 * 1_000, // 5 minutes
			gems: 10 * 60 * 1_000, // 10 minutes
			clicker2x: 5 * 60 * 1_000, // 5 minutes
			clicker3x: 5 * 60 * 1_000, // 5 minutes
			clicker5x: 5 * 60 * 1_000, // 5 minutes
			chests: 5 * 60 * 1_000, // 5 minutes
			superClickers: 5 * 60 * 1_000, // 5 minutes
			eventium: 5 * 60 * 1_000, // 5 minutes
		},
	},
};

// Funkcia na získanie aktívnej konfigurácie na základe A/B skupiny
//metóda je upravená po vyhodnotený A/B testu tak, aby sme zachovali rýchlu návratnosť do pôvodného stavu
export function getOfferConfig(group: string | null): OfferConfig {
	if (process.env.NODE_ENV === "development") {
		return devOfferConfig;
	}

	return group === "B" ? groupBOfferConfig : groupBOfferConfig;
}

/*
// Export the active configuration (switch between dev and production)
export const activeOfferConfig =
	process.env.NODE_ENV === "development"
		? devOfferConfig
		: defaultOfferConfig;
*/
